import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRANSACTION_TYPES = [
  "Strategic Advisory & M&A",
  "Private Capital & Institutional Advisory",
  "Family Office Advisory",
  "Sports Investment Advisory",
  "Real Assets & Special Situations",
  "Principal Co-Investment",
  "Other Confidential Matter",
];

const SCALES = ["£50M – £100M", "£100M – £250M", "£250M – £500M", "£500M+", "Prefer to discuss"];

const EMPTY = {
  name: "",
  organization: "",
  email: "",
  phone: "",
  transaction_type: TRANSACTION_TYPES[0],
  estimated_value: SCALES[0],
  message: "",
};

const inputCls =
  "w-full bg-[#FFFFFF] border border-[#D8D4CB] text-[#1F1E1B] text-sm px-4 py-3.5 placeholder:text-[#6E6A61]/50 focus:outline-none focus:border-[#1F1E1B]/60 transition-colors duration-300";

const labelCls = "block font-sans text-[10px] uppercase tracking-[0.24em] text-[#6E6A61] mb-2";

export const ContactSection = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg).join(" ")
            : "Submission failed. Please email enquiries@pjgcp.com directly.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 lg:py-44">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow>Contact</Eyebrow>
              <h2 className="mt-8 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1F1E1B] leading-[1.08]">
                Discuss a <span className="italic">Transaction.</span>
              </h2>
              <p className="mt-8 text-base sm:text-lg font-light leading-relaxed text-[#6E6A61]">
                For confidential enquiries regarding acquisitions, disposals, capital partnerships, sports
                investments or strategic advisory matters, please contact PJG Capital Partners.
              </p>

              <div className="mt-12 space-y-6">
                <div>
                  <p className={labelCls}>Email</p>
                  <a
                    href="mailto:enquiries@pjgcp.com"
                    data-testid="contact-email-link"
                    className="mt-2 inline-block font-serif text-xl text-[#1F1E1B] hover:opacity-60 transition-opacity duration-300"
                  >
                    enquiries@pjgcp.com
                  </a>
                </div>
                <div>
                  <p className={labelCls}>Location</p>
                  <p className="mt-2 font-serif text-xl text-[#1F1E1B]">Mayfair, London, United Kingdom</p>
                </div>
                <p className="pt-4 border-t border-[#E3E0D8] text-xs leading-relaxed text-[#6E6A61]">
                  All enquiries are treated in strict confidence and reviewed directly by a partner of the
                  firm.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.12}>
              <AnimatePresence mode="wait">
                {sent ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    data-testid="enquiry-success-alert"
                    className="h-full min-h-[420px] flex flex-col items-center justify-center text-center border border-[#1F1E1B]/25 bg-[#FFFFFF] p-12"
                  >
                    <CheckCircle2 size={44} className="text-[#1F1E1B]" strokeWidth={1.4} />
                    <h3 className="mt-6 font-serif text-3xl text-[#1F1E1B]">Enquiry Received.</h3>
                    <p className="mt-4 max-w-md text-sm leading-relaxed text-[#6E6A61]">
                      Thank you. Your enquiry has been received in confidence and a partner of the firm will
                      respond shortly.
                    </p>
                    <button
                      onClick={() => setSent(false)}
                      data-testid="enquiry-send-another-button"
                      className="mt-8 border border-[#D8D4CB] px-6 py-3 text-xs font-medium tracking-wide text-[#1F1E1B] hover:border-[#1F1E1B] transition-colors duration-300"
                    >
                      Send Another Enquiry
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, y: -12 }}
                    onSubmit={submit}
                    data-testid="enquiry-form"
                    className="border border-[#E3E0D8] bg-[#FFFFFF] p-8 lg:p-12"
                  >
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="enquiry-name" className={labelCls}>Full Name *</label>
                        <input id="enquiry-name" data-testid="enquiry-input-name" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor="enquiry-org" className={labelCls}>Organisation *</label>
                        <input id="enquiry-org" data-testid="enquiry-input-organization" required value={form.organization} onChange={set("organization")} placeholder="Family office, institution or company" className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor="enquiry-email" className={labelCls}>Email *</label>
                        <input id="enquiry-email" type="email" data-testid="enquiry-input-email" required value={form.email} onChange={set("email")} placeholder="name@institution.com" className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor="enquiry-phone" className={labelCls}>Telephone</label>
                        <input id="enquiry-phone" data-testid="enquiry-input-phone" value={form.phone} onChange={set("phone")} placeholder="+44 ..." className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor="enquiry-type" className={labelCls}>Nature of Enquiry</label>
                        <select id="enquiry-type" data-testid="enquiry-select-type" value={form.transaction_type} onChange={set("transaction_type")} className={inputCls}>
                          {TRANSACTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="enquiry-scale" className={labelCls}>Indicative Scale</label>
                        <select id="enquiry-scale" data-testid="enquiry-select-scale" value={form.estimated_value} onChange={set("estimated_value")} className={inputCls}>
                          {SCALES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="mt-6">
                      <label htmlFor="enquiry-message" className={labelCls}>Message *</label>
                      <textarea id="enquiry-message" data-testid="enquiry-input-message" required rows={5} value={form.message} onChange={set("message")} placeholder="A brief outline of the matter, in whatever detail you are comfortable sharing at this stage." className={`${inputCls} resize-none`} />
                    </div>
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="enquiry-submit-button"
                      className="group relative mt-8 inline-flex items-center justify-center overflow-hidden border border-[#1F1E1B] px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#1F1E1B] transition-colors duration-500 hover:text-[#F6F4F0] disabled:opacity-60"
                    >
                      <span className="absolute inset-0 bg-[#1F1E1B] -translate-x-full transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-0" />
                      <span className="relative z-10 inline-flex items-center gap-3">
                        {sending && <Loader2 size={16} className="animate-spin" />}
                        {sending ? "Submitting..." : "Submit Confidential Enquiry"}
                      </span>
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
